/**
 * Manufacturing → CM PO → CM supplier sites
 *
 * Bind `supplier.data` (Megaventory CM vendor) to a `public.location` used for sub work order inventory
 * when creating CM purchase orders. Overrides the PO ship-to for those work orders only.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Label } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { SupplierSearchableSelect } from "@/components/mrp/SupplierSearchableSelect";
import { supplierRowsToSearchableOptions } from "@/components/mrp/supplierSearchableSelectOptions";
import { ApiError, apiFetch } from "@/lib/api";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";

const QUERY_KEY = ["manufacturing", "cm-po", "supplier-manufacturing-locations"] as const;
const CM_SUPPLIERS_QUERY_KEY = ["manufacturing", "cm-suppliers"] as const;

interface BindingRowPayload {
  id: number;
  supplier_data_id: number;
  work_order_public_location_id: number;
  supplier_name: string | null;
  location_name: string | null;
}

interface LocationOptionPayload {
  id: number;
  name: string;
}

interface ListPayload {
  rows: BindingRowPayload[];
  location_options: LocationOptionPayload[];
}

interface CmSupplierRow {
  id: number;
  mv_supplier_id: number;
  mv_supplier_name: string | null;
}

interface EditableEntry {
  localKey: string;
  supplier_data_id: number | null;
  work_order_public_location_id: number | null;
}

function nextLocalKey(): string {
  return `row-${crypto.randomUUID()}`;
}

function emptyEntry(): EditableEntry {
  return { localKey: nextLocalKey(), supplier_data_id: null, work_order_public_location_id: null };
}

function payloadToEntries(rows: BindingRowPayload[]): EditableEntry[] {
  return rows.map((row) => ({
    localKey: `persisted-${row.id}`,
    supplier_data_id: row.supplier_data_id,
    work_order_public_location_id: row.work_order_public_location_id,
  }));
}

function fingerprintEntries(entries: EditableEntry[]): string {
  const normalized = entries
    .map((e) => ({
      supplier_data_id: e.supplier_data_id,
      work_order_public_location_id: e.work_order_public_location_id,
    }))
    .filter((e) => e.supplier_data_id != null && e.work_order_public_location_id != null)
    .sort((a, b) => Number(a.supplier_data_id) - Number(b.supplier_data_id));
  return JSON.stringify(normalized);
}

function fingerprintServer(rows: BindingRowPayload[]): string {
  const normalized = rows
    .map((r) => ({
      supplier_data_id: r.supplier_data_id,
      work_order_public_location_id: r.work_order_public_location_id,
    }))
    .sort((a, b) => a.supplier_data_id - b.supplier_data_id);
  return JSON.stringify(normalized);
}

type ModalState = { open: false } | { open: true; mode: "add" | "edit"; initial: EditableEntry };

export function CmPoSupplierManufacturingLocationsPage() {
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const queryClient = useQueryClient();
  const lastServerFingerprintRef = useRef<string | null>(null);
  const [baselineReady, setBaselineReady] = useState(false);

  const listQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiFetch<ListPayload>("/api/manufacturing/cm-po/supplier-manufacturing-locations"),
  });

  const suppliersQuery = useQuery({
    queryKey: CM_SUPPLIERS_QUERY_KEY,
    queryFn: () => apiFetch<{ rows: CmSupplierRow[] }>("/api/manufacturing/cm-suppliers"),
  });

  const [entries, setEntries] = useState<EditableEntry[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ open: false });

  useEffect(() => {
    if (!listQuery.isSuccess || !listQuery.data) return;
    const serverFp = fingerprintServer(listQuery.data.rows);
    if (serverFp === lastServerFingerprintRef.current) return;
    lastServerFingerprintRef.current = serverFp;
    setEntries(
      listQuery.data.rows.length > 0 ? payloadToEntries(listQuery.data.rows) : [],
    );
    setBaselineReady(true);
  }, [listQuery.isSuccess, listQuery.data]);

  const dirty = useMemo(() => {
    if (!baselineReady || !listQuery.isSuccess || !listQuery.data) return false;
    return fingerprintEntries(entries) !== fingerprintServer(listQuery.data.rows);
  }, [baselineReady, entries, listQuery.isSuccess, listQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (body: {
      rows: { supplier_data_id: number; work_order_public_location_id: number }[];
    }) =>
      apiFetch<ListPayload>("/api/manufacturing/cm-po/supplier-manufacturing-locations", {
        method: "PUT",
        body,
      }),
    onSuccess: (response) => {
      setSaveError(null);
      queryClient.setQueryData(QUERY_KEY, response);
    },
    onError: (err: Error) => {
      const message =
        err instanceof ApiError ? err.message : err.message.length > 0 ? err.message : "Save failed.";
      setSaveError(message);
    },
  });

  const supplierOptions = useMemo(() => {
    const rows = suppliersQuery.data?.rows ?? [];
    return supplierRowsToSearchableOptions(rows);
  }, [suppliersQuery.data?.rows]);

  const locationOptionsFromApi = listQuery.data?.location_options ?? [];

  function openAdd(): void {
    setModal({ open: true, mode: "add", initial: emptyEntry() });
  }

  function openEdit(entry: EditableEntry): void {
    setModal({ open: true, mode: "edit", initial: { ...entry, localKey: entry.localKey } });
  }

  function closeModal(): void {
    setModal({ open: false });
  }

  function removeRow(localKey: string): void {
    setEntries((previous) => previous.filter((entry) => entry.localKey !== localKey));
  }

  function commitFromModal(mode: "add" | "edit", next: EditableEntry): void {
    if (next.supplier_data_id == null || next.work_order_public_location_id == null) {
      return;
    }
    setSaveError(null);
    if (mode === "add" && entries.some((row) => row.supplier_data_id === next.supplier_data_id)) {
      setSaveError("That supplier is already in the list.");
      return;
    }
    setEntries((previous) => {
      if (mode === "add") {
        return [...previous, { ...next, localKey: nextLocalKey() }];
      }
      return previous.map((row) =>
        row.localKey === next.localKey ? { ...next, localKey: row.localKey } : row,
      );
    });
    closeModal();
  }

  async function handleSave(): Promise<void> {
    if (!hubWritesAllowed) return;
    setSaveError(null);
    const rows: { supplier_data_id: number; work_order_public_location_id: number }[] = [];
    const seen = new Set<number>();
    for (const entry of entries) {
      if (entry.supplier_data_id == null || entry.work_order_public_location_id == null) {
        setSaveError("Each row needs a supplier and a manufacturing location.");
        return;
      }
      if (seen.has(entry.supplier_data_id)) {
        setSaveError("Each supplier may only appear once.");
        return;
      }
      seen.add(entry.supplier_data_id);
      rows.push({
        supplier_data_id: entry.supplier_data_id,
        work_order_public_location_id: entry.work_order_public_location_id,
      });
    }
    await saveMutation.mutateAsync({ rows });
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg">CM supplier manufacturing sites</CardTitle>
              <p className="mt-2 max-w-3xl text-sm text-slate-600">
                Tie a Megaventory supplier row to the inventory location where that contract manufacturer builds sub work
                orders. This applies when you create a CM purchase order: the purchase order can still ship to SGS (or
                another site), while work orders use the location bound here. Suppliers not listed fall back to Suntronic /
                Kodiak / TenX name rules, then to the PO ship-to you pick on the form.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={
                  saveMutation.isPending || !hubWritesAllowed || suppliersQuery.isLoading || listQuery.isLoading
                }
                onClick={() => openAdd()}
              >
                <Plus className="mr-1 h-4 w-4" aria-hidden />
                Add binding
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={
                  saveMutation.isPending ||
                  listQuery.isLoading ||
                  listQuery.isPending ||
                  !hubWritesAllowed ||
                  !dirty
                }
                onClick={() => void handleSave()}
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    Saving…
                  </>
                ) : (
                  "Save bindings"
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody className="space-y-3">
          {!hubWritesAllowed ? (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              You have read-only access to Manufacturing. Assign read-write Manufacturing access if you should edit these
              bindings.
            </p>
          ) : null}
          {saveError ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">{saveError}</p>
          ) : null}
          {listQuery.isLoading || listQuery.isPending || suppliersQuery.isLoading ? (
            <p className="py-10 text-center text-sm text-slate-500">
              <Loader2 className="mr-2 inline h-5 w-5 animate-spin text-slate-400" aria-hidden />
              Loading…
            </p>
          ) : entries.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">
              No supplier bindings yet — add one per contract manufacturer that should not use the PO ship-to for sub work
              orders.
              {hubWritesAllowed ? (
                <span className="mt-4 block">
                  <Button type="button" size="sm" variant="secondary" onClick={() => openAdd()}>
                    <Plus className="mr-1 h-4 w-4" aria-hidden />
                    Add binding
                  </Button>
                </span>
              ) : null}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {entries.map((entry) => {
                const supplierLabel =
                  supplierOptions.find((option) => Number(option.value) === entry.supplier_data_id)?.label ?? "—";
                const locationLabel =
                  locationOptionsFromApi.find((option) => option.id === entry.work_order_public_location_id)?.name ??
                  "—";
                return (
                  <li key={entry.localKey} className="flex flex-wrap items-start justify-between gap-2 px-4 py-3">
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="text-sm font-semibold text-slate-900">{supplierLabel}</div>
                      <div className="text-sm text-slate-600">
                        Sub work order inventory: <span className="font-medium">{locationLabel}</span>
                      </div>
                    </div>
                    {hubWritesAllowed ? (
                      <div className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          aria-label={`Edit binding for supplier ${String(entry.supplier_data_id)}`}
                          disabled={saveMutation.isPending}
                          onClick={() => openEdit(entry)}
                          className="inline-flex items-center rounded-md border border-transparent p-2 text-slate-500 transition-colors hover:border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </button>
                        <button
                          type="button"
                          aria-label={`Remove binding for supplier ${String(entry.supplier_data_id)}`}
                          disabled={saveMutation.isPending}
                          onClick={() => removeRow(entry.localKey)}
                          className="inline-flex items-center rounded-md border border-transparent p-2 text-slate-500 transition-colors hover:border-slate-200 hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      {modal.open ? (
        <BindingModal
          mode={modal.mode}
          initial={modal.initial}
          hubWritesAllowed={hubWritesAllowed}
          saveMutationPending={saveMutation.isPending}
          locationOptions={locationOptionsFromApi}
          takenSupplierIds={entries
            .filter((row) => modal.mode === "add" || row.localKey !== modal.initial.localKey)
            .map((row) => row.supplier_data_id)
            .filter((id): id is number => id != null)}
          onClose={closeModal}
          onCommit={(entry) => commitFromModal(modal.mode, entry)}
        />
      ) : null}
    </div>
  );
}

function BindingModal({
  mode,
  initial,
  hubWritesAllowed,
  saveMutationPending,
  locationOptions,
  takenSupplierIds,
  onClose,
  onCommit,
}: {
  mode: "add" | "edit";
  initial: EditableEntry;
  hubWritesAllowed: boolean;
  saveMutationPending: boolean;
  locationOptions: LocationOptionPayload[];
  takenSupplierIds: number[];
  onClose: () => void;
  onCommit: (entry: EditableEntry) => void;
}) {
  const [supplierSearch, setSupplierSearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [supplierId, setSupplierId] = useState<string>(initial.supplier_data_id != null ? String(initial.supplier_data_id) : "");
  const [locationId, setLocationId] = useState<string>(
    initial.work_order_public_location_id != null ? String(initial.work_order_public_location_id) : "",
  );
  const [modalError, setModalError] = useState<string | null>(null);

  const takenSet = useMemo(() => new Set(takenSupplierIds), [takenSupplierIds]);

  const locationSelectOptions = useMemo(
    () => locationOptions.map((row) => ({ value: String(row.id), label: row.name })),
    [locationOptions],
  );

  useEffect(() => {
    setSupplierId(initial.supplier_data_id != null ? String(initial.supplier_data_id) : "");
    setLocationId(initial.work_order_public_location_id != null ? String(initial.work_order_public_location_id) : "");
    setModalError(null);
    setSupplierSearch("");
    setLocationSearch("");
  }, [initial.localKey, initial.supplier_data_id, initial.work_order_public_location_id]);

  const disabled = !hubWritesAllowed || saveMutationPending;

  function validateAndCommit(): void {
    setModalError(null);
    const supplierNum = Number.parseInt(supplierId, 10);
    const locationNum = Number.parseInt(locationId, 10);
    if (!Number.isFinite(supplierNum) || supplierNum <= 0) {
      setModalError("Pick a supplier.");
      return;
    }
    if (!Number.isFinite(locationNum) || locationNum <= 0) {
      setModalError("Pick a manufacturing location.");
      return;
    }
    if (mode === "add" && takenSet.has(supplierNum)) {
      setModalError("That supplier already has a binding.");
      return;
    }
    onCommit({
      ...initial,
      supplier_data_id: supplierNum,
      work_order_public_location_id: locationNum,
    });
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex min-h-0 items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cm-po-supplier-binding-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <Card className="relative z-[101] my-4 w-full max-w-lg shadow-xl">
        <CardHeader>
          <CardTitle id="cm-po-supplier-binding-title" className="text-base">
            {mode === "add" ? "Add supplier binding" : "Edit supplier binding"}
          </CardTitle>
          <p className="mt-1 text-xs text-slate-500">
            Only Megaventory-linked suppliers and locations with a Megaventory inventory id can be saved.
          </p>
        </CardHeader>
        <CardBody className="space-y-4 border-t border-slate-100">
          {modalError ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">{modalError}</p>
          ) : null}
          <div className="space-y-2">
            <Label>Supplier</Label>
            <SupplierSearchableSelect
              value={supplierId}
              excludeValues={takenSupplierIds.map((id) => String(id))}
              placeholder="Select supplier…"
              searchPlaceholder="Search suppliers…"
              searchValue={supplierSearch}
              onSearchChange={setSupplierSearch}
              disabled={disabled || mode === "edit"}
              onValueChange={(option) => setSupplierId(option?.value ?? "")}
            />
            {mode === "edit" ? (
              <p className="text-xs text-slate-500">
                To switch suppliers, remove this row and add a new binding.
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label>Manufacturing location</Label>
            <SearchableSelect
              value={locationId}
              presentation="panelSearch"
              placeholder="Select location…"
              searchPlaceholder="Search locations…"
              options={locationSelectOptions}
              searchValue={locationSearch}
              onSearchChange={setLocationSearch}
              disabled={disabled}
              onValueChange={(option) => setLocationId(option?.value ?? "")}
            />
          </div>
        </CardBody>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saveMutationPending}>
            Cancel
          </Button>
          <Button type="button" onClick={validateAndCommit} disabled={disabled}>
            {mode === "add" ? "Add" : "Apply"}
          </Button>
        </div>
      </Card>
    </div>,
    document.body,
  );
}
